/**
 * The role tag on the users table.
 *
 * Previously a bare antd <Tag> with no color prop, so every role -- Recruiter
 * and Super Admin alike -- read with identical flat weight. Reuses
 * statusPalette.ts's `active`/`idle` tones rather than a new palette: Admin
 * and Super Admin get the same ink-on-transparent treatment already used for
 * an in-flight candidate status (not a new color, just applied to roles),
 * Recruiter and Hiring Manager get the plainer muted tone.
 *
 * Clicking the tag opens the same click-to-reveal popover as CaptionInfo,
 * via its `children` trigger slot -- one disclosure pattern, not two.
 */

import { memo } from 'react';
import { Tag } from 'antd';
import CaptionInfo from '../../../../components/common/caption-info';
import { toneStyle } from '../../../jobs/utils/statusPalette';
import {
  ROLE_DESCRIPTIONS,
  ROLE_TAG_WEIGHT,
  roleSeesLine,
} from '../../utils/roleCapabilities';
import { roleLabelMapping, UserRole } from '../../utils/role';
import styles from './index.module.css';

export interface RoleTagProps {
  /** Raw role string as it comes off a user record -- not guaranteed to
   * match a known UserRole at runtime, same assumption the table's previous
   * inline render made. */
  role: string;
}

export const RoleTag = memo(({ role }: RoleTagProps) => {
  const knownRole = role as UserRole;
  const label = roleLabelMapping[knownRole as keyof typeof roleLabelMapping];

  if (!label) {
    return <Tag className={styles.tag}>Unknown</Tag>;
  }

  const weight = ROLE_TAG_WEIGHT[knownRole];
  const tone = toneStyle(weight === 'elevated' ? 'active' : 'idle');
  const seesLine = roleSeesLine(knownRole);
  const description = `${ROLE_DESCRIPTIONS[knownRole]}${seesLine ? ` ${seesLine}` : ''}`;

  return (
    <CaptionInfo text={description} label={`About the ${label} role`}>
      <Tag
        className={styles.tag}
        style={{
          color: tone.color,
          background: tone.background,
          borderColor: tone.borderColor,
        }}
        role="button"
        tabIndex={0}
        aria-label={`About the ${label} role`}
        onKeyDown={(event) => {
          // A <Tag> renders a <span>, which -- unlike a <button> -- does not
          // fire a click on Enter/Space by itself. Without this, the tag
          // would be focusable but not actually operable from a keyboard.
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
      >
        {label}
      </Tag>
    </CaptionInfo>
  );
});

RoleTag.displayName = 'RoleTag';

export default RoleTag;
